// Canlı terazi (elektronik tartı) okuma — WebSerial API. Tarayıcı destekliyorsa
// (Chrome/Edge, HTTPS) seri porttan sürekli ağırlık akışını okur; desteklemiyorsa
// manuel girişe düşülür. Bağlantı modül seviyesinde singleton tutulur; tartma
// diyaloğu her açıldığında yeniden bağlanmaya gerek kalmaz.
import { useEffect, useState } from "react";

let _port = null;
let _reader = null;
let _abort = false;
const _listeners = new Set();
let _state = { connected: false, weight: 0, stable: true, unit: "kg", error: null };

function _emit() {
  for (const fn of _listeners) {
    try { fn(_state); } catch (e) { /* ignore */ }
  }
}
function _set(patch) { _state = { ..._state, ...patch }; _emit(); }

export function scaleSupported() {
  return typeof navigator !== "undefined" && "serial" in navigator;
}

// Bir seri kareden ağırlık çözümler. Yaygın formatlar: "ST,GS,+ 001.234 kg",
// "US,+0000.00kg", "+0001.250 kg", "  1.234". Sayıyı çeker; birim gram ise kg'a çevirir.
function parseFrame(line) {
  const s = (line || "").trim();
  if (!s) return null;
  const m = s.match(/([+-]?\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  const val = parseFloat(m[1].replace(",", "."));
  if (isNaN(val)) return null;
  const low = s.toLowerCase();
  let unit = "kg";
  if (low.includes("kg")) unit = "kg";
  else if (/(?:^|[^k])g\b/.test(low) || low.endsWith("g")) unit = "g";
  const weight = unit === "g" ? val / 1000 : val;
  const unstable = /\b(us|mo)\b/i.test(s); // US=unstable, MO=motion
  return { weight: Math.max(0, weight), stable: !unstable, unit };
}

async function _readLoop() {
  const decoder = new TextDecoder();
  let buf = "";
  while (!_abort && _port && _port.readable) {
    _reader = _port.readable.getReader();
    try {
      while (true) {
        const { value, done } = await _reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.search(/[\r\n]/)) >= 0) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 1);
          const p = parseFrame(frame);
          if (p) _set({ weight: p.weight, stable: p.stable, unit: p.unit, error: null });
        }
        if (buf.length > 300) buf = buf.slice(-100);
      }
    } catch (e) {
      _set({ error: String(e && e.message ? e.message : e) });
    } finally {
      try { _reader.releaseLock(); } catch (e) { /* ignore */ }
      _reader = null;
    }
  }
}

export async function connectScale(baudRate = 9600) {
  if (!scaleSupported()) {
    throw new Error("Bu tarayıcı tartı bağlantısını desteklemiyor (WebSerial). Chrome veya Edge kullanın.");
  }
  if (_port) return true;
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate });
  _port = port;
  _abort = false;
  _set({ connected: true, error: null, weight: 0 });
  _readLoop();
  return true;
}

export async function autoConnectScale(baudRate = 9600) {
  // Önceden izin verilmiş bir porta (kullanıcı daha önce bir kez bağlanmışsa) jest gerektirmeden
  // yeniden bağlanır. İlk izin tarayıcı güvenliği gereği kullanıcı tıklamasıyla verilir.
  if (!scaleSupported() || _port) return false;
  try {
    const ports = await navigator.serial.getPorts();
    if (!ports || !ports.length) return false;
    const port = ports[0];
    await port.open({ baudRate });
    _port = port;
    _abort = false;
    _set({ connected: true, error: null, weight: 0 });
    _readLoop();
    return true;
  } catch (e) {
    return false;
  }
}

export async function disconnectScale() {
  _abort = true;
  try { if (_reader) await _reader.cancel(); } catch (e) { /* ignore */ }
  try { if (_port) await _port.close(); } catch (e) { /* ignore */ }
  _port = null;
  _reader = null;
  _set({ connected: false, weight: 0, stable: true });
}

export function useScale() {
  const [state, setState] = useState(_state);
  useEffect(() => {
    const fn = (s) => setState({ ...s });
    _listeners.add(fn);
    fn(_state);
    return () => { _listeners.delete(fn); };
  }, []);
  return state;
}
